import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import PolicyTab from '../../../components/common/PolicyTab';
import ClassContextPicker, { useSchoolConfig } from '../../../components/common/ClassContextPicker';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';

// ─── Helpers ────────────────────────────────────────────────────────────────
const newId = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const SOURCE_BADGE = {
  manual:       { label: 'Manual',       bs: 'secondary', icon: 'bi-pencil' },
  ai:           { label: 'AI',           bs: 'primary',   icon: 'bi-robot' },
  word_import:  { label: 'Word Import',  bs: 'info',      icon: 'bi-file-earmark-word' },
  pdf_import:   { label: 'PDF Import',   bs: 'warning',   icon: 'bi-file-earmark-pdf' },
  reused:       { label: 'Reused',       bs: 'success',   icon: 'bi-arrow-repeat' },
};

// ─── Main ───────────────────────────────────────────────────────────────────
export default function EducationCourseContentPage() {
  const { user } = useAuth();
  const role = user?.roleKey || '';
  const canEdit  = ['admin','manager','team_lead','faculty','teacher'].includes(role) || user?.userType === 'super_admin';
  const canAdmin = ['admin','manager'].includes(role) || user?.userType === 'super_admin';

  // Top-level navigation: 'list' = picker + course list; 'detail' = open course; 'policy'
  const [view, setView]               = useState('list');
  const [filter, setFilter]           = useState({ classGrade: '', subject: '' });
  const [courses, setCourses]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [course, setCourse]           = useState(null);   // currently-open course (full doc)
  const [openTopic, setOpenTopic]     = useState(null);   // { moduleId, topicId } currently being edited
  const [toast, setToast]             = useState(null);
  const [saving, setSaving]           = useState(false);

  const cfg = useSchoolConfig();

  // ─── Load courses by filter ─────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.classGrade) params.classGrade = filter.classGrade;
      if (filter.subject)    params.subject    = filter.subject;
      const r = await api.get('/education/courses', { params });
      setCourses(r.data?.records || []);
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load courses.' });
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { if (view === 'list') fetchCourses(); }, [view, fetchCourses]);

  // ─── Open a course ──────────────────────────────────────────────────────
  const openCourse = async (id) => {
    try {
      const r = await api.get(`/education/courses/${id}`);
      setCourse(r.data?.data || r.data);
      setView('detail');
      setOpenTopic(null);
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to open course.' });
    }
  };

  // Reload current course after server edits
  const reloadCourse = async () => {
    if (!course?._id) return;
    const r = await api.get(`/education/courses/${course._id}`);
    setCourse(r.data?.data || r.data);
  };

  return (
    <div>
      <PageHeader title="Course Content" icon="bi-collection-play"
        subtitle="Per-class, per-subject course tree — modules, topics & content blocks"
      />
      {toast && <AlertToast type={toast.type} message={toast.msg} show={true} onClose={() => setToast(null)} />}

      {view !== 'policy' && (
        <ModuleHeaderStrip
          icon="bi-collection-play"
          label="Course Content"
          chips={[
            { label: 'Active classes', value: cfg.classes.length || 'configure in School Config' },
            { label: 'Subjects',       value: cfg.subjects.length || 'configure in School Config' },
            { label: 'Courses',        value: courses.length },
          ]}
        />
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${view === 'list'   ? 'active' : ''}`} onClick={() => setView('list')}>
          <i className="bi bi-grid me-1"/>Courses
        </button></li>
        {course && view !== 'policy' && (
          <li className="nav-item"><button className={`nav-link ${view === 'detail' ? 'active' : ''}`} onClick={() => setView('detail')}>
            <i className="bi bi-folder-symlink me-1"/>{course.title}
          </button></li>
        )}
        <li className="nav-item"><button className={`nav-link ${view === 'policy' ? 'active' : ''}`} onClick={() => setView('policy')}>
          <i className="bi bi-journal-text me-1"/>Policy & Manual
        </button></li>
      </ul>

      {view === 'list'   && <CourseListView filter={filter} setFilter={setFilter} courses={courses} loading={loading}
                                            openCourse={openCourse} canEdit={canEdit} canAdmin={canAdmin}
                                            onCreated={fetchCourses} setToast={setToast} />}
      {view === 'detail' && course && <CourseDetailView course={course} canEdit={canEdit} canAdmin={canAdmin}
                                            reloadCourse={reloadCourse} setToast={setToast}
                                            openTopic={openTopic} setOpenTopic={setOpenTopic} />}
      {view === 'policy' && <PolicyTab moduleKey="advanced_course_content" label="Course Content — Manual & Policy" />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LIST VIEW: filter + courses table + create new
// ════════════════════════════════════════════════════════════════════════════
function CourseListView({ filter, setFilter, courses, loading, openCourse, canEdit, canAdmin, onCreated, setToast }) {
  const cfg = useSchoolConfig();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ classGrade: '', subject: '', title: '', description: '', board: '', academicYear: '' });
  const [saving, setSaving] = useState(false);

  // Pre-fill new-course form from the active filter for convenience.
  useEffect(() => {
    if (showCreate) setForm(p => ({ ...p, classGrade: filter.classGrade || '', subject: filter.subject || '' }));
  }, [showCreate, filter]);

  const create = async () => {
    if (!form.classGrade || !form.subject || !form.title) {
      return setToast({ type: 'danger', msg: 'Class, subject and title are required.' });
    }
    setSaving(true);
    try {
      await api.post('/education/courses', form);
      setToast({ type: 'success', msg: 'Course created.' });
      setShowCreate(false); setForm({ classGrade: '', subject: '', title: '', description: '', board: '', academicYear: '' });
      onCreated();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Create failed.' });
    }
    setSaving(false);
  };

  return (
    <>
      <div className="card mb-3">
        <div className="card-body">
          <ClassContextPicker
            value={filter} onChange={setFilter}
            fields={['classGrade','subject']} compact
          />
          <div className="text-end mt-2">
            {canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
                <i className="bi bi-plus-lg me-1"/>New Course
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4"><LoadingSpinner /></div>
          ) : courses.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-collection-play fs-1 d-block mb-2"/>
              No courses yet for the selected filter. {canEdit && <span>Click <strong>New Course</strong> to create one.</span>}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Class</th>
                    <th>Subject</th>
                    <th>Board</th>
                    <th>Year</th>
                    <th style={{ width: 90 }}>Modules</th>
                    <th style={{ width: 90 }}>Topics</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map(c => (
                    <tr key={c._id} role="button" onClick={() => openCourse(c._id)}>
                      <td className="fw-semibold">{c.title}</td>
                      <td>{c.classGrade}</td>
                      <td>{c.subject}</td>
                      <td>{c.board || '—'}</td>
                      <td>{c.academicYear || '—'}</td>
                      <td>{c.moduleCount || 0}</td>
                      <td>{c.topicCount || 0}</td>
                      <td>
                        <span className={`badge bg-${c.status === 'published' ? 'success' : c.status === 'archived' ? 'secondary' : 'warning'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="small text-muted">{new Date(c.updatedAt).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New-course modal */}
      {showCreate && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Course</h5>
                <button className="btn-close" onClick={() => setShowCreate(false)}/>
              </div>
              <div className="modal-body">
                <ClassContextPicker
                  value={{ classGrade: form.classGrade, subject: form.subject }}
                  onChange={v => setForm(p => ({ ...p, classGrade: v.classGrade, subject: v.subject }))}
                  fields={['classGrade','subject']}
                  required={['classGrade','subject']}
                />
                <div className="row g-2 mt-2">
                  <div className="col-md-8">
                    <label className="form-label small mb-1">Course Title <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={form.title}
                      onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">Academic Year</label>
                    <input className="form-control form-control-sm" placeholder="2026-27" value={form.academicYear}
                      onChange={e => setForm(p => ({ ...p, academicYear: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">Board</label>
                    <input className="form-control form-control-sm" placeholder="CBSE / ICSE / State..." value={form.board}
                      onChange={e => setForm(p => ({ ...p, board: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small mb-1">Description</label>
                    <textarea className="form-control form-control-sm" rows={2} value={form.description}
                      onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCreate(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={create} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"/>}Create Course
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DETAIL VIEW: tree of modules → topics, plus topic editor
// ════════════════════════════════════════════════════════════════════════════
function CourseDetailView({ course, canEdit, canAdmin, reloadCourse, setToast, openTopic, setOpenTopic }) {
  const [showReuse, setShowReuse]   = useState(false);
  const [importingWord, setImportingWord] = useState(false);
  const fileRef = useRef(null);

  const addModule = async () => {
    const title = window.prompt('Module title?');
    if (!title) return;
    try {
      await api.post(`/education/courses/${course._id}/modules`, { title });
      reloadCourse();
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed.' }); }
  };
  const addTopic = async (moduleId) => {
    const title = window.prompt('Topic title?');
    if (!title) return;
    try {
      await api.post(`/education/courses/${course._id}/modules/${moduleId}/topics`, { title });
      reloadCourse();
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed.' }); }
  };
  const removeModule = async (moduleId) => {
    if (!window.confirm('Remove this module and all its topics?')) return;
    try {
      await api.delete(`/education/courses/${course._id}/modules/${moduleId}`);
      reloadCourse();
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed.' }); }
  };
  const removeTopic = async (moduleId, topicId) => {
    if (!window.confirm('Remove this topic?')) return;
    try {
      await api.delete(`/education/courses/${course._id}/modules/${moduleId}/topics/${topicId}`);
      reloadCourse();
      if (openTopic?.topicId === topicId) setOpenTopic(null);
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed.' }); }
  };

  const handleWordUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingWord(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/education/courses/${course._id}/import-word`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = r.data?.data || r.data;
      setToast({ type: 'success', msg: `Imported ${d.modulesImported} module(s), ${d.topicsImported} topic(s).` });
      reloadCourse();
    } catch (err) {
      setToast({ type: 'danger', msg: err.response?.data?.message || 'Word import failed.' });
    }
    if (fileRef.current) fileRef.current.value = '';
    setImportingWord(false);
  };

  const totalTopics = (course.modules || []).reduce((s, m) => s + (m.topics?.length || 0), 0);
  const filledTopics = (course.modules || []).reduce(
    (s, m) => s + (m.topics || []).filter(t => t.hasContent || (t.contentBlocks?.length > 0)).length, 0
  );

  return (
    <>
      {/* Course banner */}
      <div className="card mb-3" style={{ background: 'linear-gradient(135deg, #ede9fe, #fae8ff)', border: 'none' }}>
        <div className="card-body py-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h5 className="mb-0 fw-bold">{course.title}</h5>
              <div className="small text-muted mt-1">
                <strong>Class:</strong> {course.classGrade}
                <span className="mx-2">·</span>
                <strong>Subject:</strong> {course.subject}
                {course.board && <><span className="mx-2">·</span><strong>Board:</strong> {course.board}</>}
                {course.academicYear && <><span className="mx-2">·</span><strong>Year:</strong> {course.academicYear}</>}
              </div>
              <div className="small text-muted mt-1">
                <span className="badge bg-light text-dark border me-1">{(course.modules || []).length} modules</span>
                <span className="badge bg-light text-dark border me-1">{totalTopics} topics</span>
                <span className="badge bg-success-subtle text-success">{filledTopics} with content</span>
              </div>
            </div>
            {canEdit && (
              <div className="d-flex gap-1 flex-wrap">
                <button className="btn btn-outline-primary btn-sm" onClick={addModule}>
                  <i className="bi bi-plus me-1"/>Add Module
                </button>
                <button className="btn btn-outline-success btn-sm" onClick={() => setShowReuse(true)}>
                  <i className="bi bi-arrow-repeat me-1"/>Reuse from another course
                </button>
                <label className="btn btn-outline-info btn-sm mb-0">
                  {importingWord ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-file-earmark-word me-1"/>}
                  Import Word (.docx)
                  <input ref={fileRef} type="file" hidden accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleWordUpload} disabled={importingWord} />
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Left: tree */}
        <div className="col-lg-5">
          <div className="card">
            <div className="card-header py-2 fw-semibold"><i className="bi bi-list-nested me-1"/>Modules & Topics</div>
            <div className="card-body p-2">
              {(course.modules || []).length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  <i className="bi bi-folder fs-2 d-block mb-2"/>
                  No modules yet. {canEdit && 'Click "Add Module" above.'}
                </div>
              ) : (
                course.modules.map((m, mi) => (
                  <div key={m._id} className="border rounded mb-2 p-2" style={{ background: '#fafbfc' }}>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-primary">M{mi + 1}</span>
                      <span className="fw-semibold flex-grow-1">{m.title}</span>
                      {canEdit && (
                        <>
                          <button className="btn btn-outline-success btn-sm" onClick={() => addTopic(m._id)} title="Add topic">
                            <i className="bi bi-plus"/>
                          </button>
                          <button className="btn btn-outline-danger btn-sm" onClick={() => removeModule(m._id)} title="Remove module">
                            <i className="bi bi-trash"/>
                          </button>
                        </>
                      )}
                    </div>
                    <div className="ms-3 mt-2">
                      {(m.topics || []).length === 0 ? (
                        <div className="small text-muted">No topics. {canEdit && 'Click + to add one.'}</div>
                      ) : (
                        m.topics.map((t, ti) => {
                          const isOpen = openTopic?.topicId === t._id;
                          return (
                            <div key={t._id}
                              className={`d-flex align-items-center gap-2 py-1 px-2 rounded ${isOpen ? 'bg-primary bg-opacity-10' : ''}`}
                              role="button"
                              onClick={() => setOpenTopic({ moduleId: m._id, topicId: t._id })}
                            >
                              <span className="text-muted small" style={{ minWidth: 28 }}>{ti + 1}.</span>
                              <span className="flex-grow-1 small">{t.title}</span>
                              {(t.hasContent || t.contentBlocks?.length > 0) && (
                                <i className="bi bi-check-circle-fill text-success small" title="Has content"/>
                              )}
                              {canEdit && (
                                <button className="btn btn-outline-danger btn-sm py-0 px-1" onClick={(e) => { e.stopPropagation(); removeTopic(m._id, t._id); }}>
                                  <i className="bi bi-x"/>
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: topic editor */}
        <div className="col-lg-7">
          {openTopic ? (
            <TopicEditor
              course={course}
              moduleId={openTopic.moduleId}
              topicId={openTopic.topicId}
              canEdit={canEdit}
              reloadCourse={reloadCourse}
              setToast={setToast}
            />
          ) : (
            <div className="card">
              <div className="card-body text-center text-muted py-5">
                <i className="bi bi-arrow-left-circle fs-1 d-block mb-2"/>
                Pick a topic from the left to view or edit its content.
              </div>
            </div>
          )}
        </div>
      </div>

      {showReuse && (
        <ReuseDialog
          course={course}
          onClose={() => setShowReuse(false)}
          reloadCourse={() => { reloadCourse(); setShowReuse(false); }}
          setToast={setToast}
        />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TOPIC EDITOR — content blocks + AI generate per topic
// ════════════════════════════════════════════════════════════════════════════
function TopicEditor({ course, moduleId, topicId, canEdit, reloadCourse, setToast }) {
  const m = (course.modules || []).find(x => x._id === moduleId);
  const t = m?.topics?.find(x => x._id === topicId);

  const [blocks, setBlocks]       = useState(t?.contentBlocks || []);
  const [title, setTitle]         = useState(t?.title || '');
  const [estHours, setEstHours]   = useState(t?.estimatedHours || 0);
  const [aiPrompt, setAiPrompt]   = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving]       = useState(false);
  // Block-ids currently shown as rendered preview (vs. raw HTML in a textarea).
  // AI-generated and imported blocks default to preview so editors can see
  // the actual images / formatting immediately.
  const [previewIds, setPreviewIds] = useState(() => new Set());
  const togglePreview = (id) => setPreviewIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Snapshot of the server-side blocks at last load/save. Used to compute
  // which blocks have unsaved local edits — drives the "modified" badge and
  // the dirty count on the Save button.
  const [savedSnapshot, setSavedSnapshot] = useState({ blocks: [], title: '', estHours: 0 });

  // Reset state when switching topics. Auto-mark non-manual blocks as preview.
  useEffect(() => {
    const initial = t?.contentBlocks || [];
    setBlocks(initial);
    setTitle(t?.title || '');
    setEstHours(t?.estimatedHours || 0);
    setAiPrompt('');
    setSavedSnapshot({ blocks: initial, title: t?.title || '', estHours: t?.estimatedHours || 0 });
    const initialPreviewIds = new Set(
      initial.filter(b => b.type === 'text' && (b.source === 'ai' || b.source === 'word_import' || b.source === 'reused')).map(b => b._id)
    );
    setPreviewIds(initialPreviewIds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId]);

  // Compute dirty block IDs and total dirty count (blocks + topic-level fields).
  const savedById = {};
  (savedSnapshot.blocks || []).forEach(b => { savedById[b._id] = b; });
  const dirtyBlockIds = new Set();
  const seenIds = new Set();
  blocks.forEach(b => {
    seenIds.add(b._id);
    const saved = savedById[b._id];
    if (!saved) { dirtyBlockIds.add(b._id); return; }   // newly added
    if (saved.content !== b.content || saved.title !== b.title || saved.type !== b.type) {
      dirtyBlockIds.add(b._id);
    }
  });
  // Removed blocks count as dirty too (we just don't show a badge for them since they're gone).
  const removedCount = (savedSnapshot.blocks || []).filter(b => !seenIds.has(b._id)).length;
  const titleDirty   = (savedSnapshot.title || '') !== (title || '');
  const hoursDirty   = (savedSnapshot.estHours || 0) !== (estHours || 0);
  const dirtyCount   = dirtyBlockIds.size + removedCount + (titleDirty ? 1 : 0) + (hoursDirty ? 1 : 0);
  const hasUnsaved   = dirtyCount > 0;

  if (!t) return <div className="card"><div className="card-body text-center text-muted">Topic not found.</div></div>;

  const addBlock = (type, contentSeed = '') => {
    setBlocks(prev => [...prev, { _id: newId('blk'), type, content: contentSeed, title: '', source: 'manual' }]);
  };
  const removeBlock = (id) => setBlocks(prev => prev.filter(b => b._id !== id));
  const updateBlock = (id, patch) => setBlocks(prev => prev.map(b => b._id === id ? { ...b, ...patch } : b));

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/education/courses/${course._id}/modules/${moduleId}/topics/${topicId}/generate`, { prompt: aiPrompt });
      const html = r.data?.data?.html || r.data?.html || '';
      if (!html) {
        setToast({ type: 'danger', msg: 'AI returned empty content.' });
      } else {
        const id = newId('blk');
        setBlocks(prev => [...prev, { _id: id, type: 'text', content: html, title: 'AI-generated', source: 'ai', meta: { prompt: aiPrompt } }]);
        // Show the AI block in rendered preview by default so the user sees
        // the images straight away.
        setPreviewIds(prev => { const n = new Set(prev); n.add(id); return n; });
        setToast({ type: 'success', msg: 'AI content added.' });
        setAiPrompt('');
      }
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'AI generation failed.' });
    }
    setGenerating(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/education/courses/${course._id}/modules/${moduleId}/topics/${topicId}`, {
        title, estimatedHours: estHours, contentBlocks: blocks,
      });
      // Snapshot the current state as the new "saved" baseline so the dirty
      // indicators clear immediately without waiting for reloadCourse.
      setSavedSnapshot({ blocks: blocks.map(b => ({ ...b })), title, estHours });
      setToast({ type: 'success', msg: dirtyCount > 0 ? `Saved ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}.` : 'Topic saved.' });
      reloadCourse();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' });
    }
    setSaving(false);
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span className="fw-semibold">
          <i className="bi bi-file-earmark-text me-1 text-primary"/>{m.title} · {t.title}
          {hasUnsaved && (
            <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>
              <i className="bi bi-pencil-fill me-1"/>{dirtyCount} unsaved
            </span>
          )}
        </span>
        {canEdit && (
          <button className={`btn btn-${hasUnsaved ? 'warning' : 'success'} btn-sm`} onClick={save} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-save me-1"/>}
            {hasUnsaved ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save Topic'}
          </button>
        )}
      </div>
      <div className="card-body">
        {canEdit && (
          <div className="row g-2 mb-3">
            <div className="col-md-9">
              <label className="form-label small mb-1">Topic Title</label>
              <input className="form-control form-control-sm" value={title} onChange={e => setTitle(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Est. hours</label>
              <input type="number" min="0" step="0.5" className="form-control form-control-sm" value={estHours} onChange={e => setEstHours(parseFloat(e.target.value) || 0)} />
            </div>
          </div>
        )}

        {canEdit && (
          <div className="border rounded p-2 mb-3" style={{ background: '#f8fafc' }}>
            <div className="small fw-semibold mb-2 text-muted text-uppercase">Add Content</div>
            <div className="d-flex flex-wrap gap-2">
              <button className="btn btn-outline-secondary btn-sm" onClick={() => addBlock('text')}><i className="bi bi-text-paragraph me-1"/>Text / HTML</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => addBlock('image')}><i className="bi bi-image me-1"/>Image URL</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => addBlock('video')}><i className="bi bi-camera-video me-1"/>Video URL</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => addBlock('pdf')}><i className="bi bi-file-earmark-pdf me-1"/>PDF URL</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => addBlock('link')}><i className="bi bi-link-45deg me-1"/>External Link</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => addBlock('code')}><i className="bi bi-code-slash me-1"/>Code</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => addBlock('embed')}><i className="bi bi-window me-1"/>Embed (HTML)</button>
            </div>

            <div className="mt-3">
              <div className="small fw-semibold text-muted text-uppercase mb-1">Or generate with AI</div>
              <div className="input-group input-group-sm">
                <input className="form-control" placeholder="Optional: extra instruction for the AI" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} />
                <button className="btn btn-primary" onClick={generate} disabled={generating}>
                  {generating ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-robot me-1"/>}
                  Generate Content
                </button>
              </div>
              <small className="text-muted">Uses topic title and class context as the AI prompt. Generated content is appended as a new block.</small>
            </div>
          </div>
        )}

        {blocks.length === 0 ? (
          <div className="text-center text-muted py-4 small">
            <i className="bi bi-inbox fs-2 d-block mb-2"/>
            No content yet. {canEdit && 'Use the buttons above to add a block or ask the AI.'}
          </div>
        ) : (
          blocks.map((b, i) => {
            const sb = SOURCE_BADGE[b.source] || SOURCE_BADGE.manual;
            return (
              <div key={b._id} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>
                    <span className={`badge bg-${sb.bs} me-2`}><i className={`bi ${sb.icon} me-1`}/>{sb.label}</span>
                    <span className="text-muted small me-2">#{i + 1} · {b.type}</span>
                    {dirtyBlockIds.has(b._id) && (
                      <span className="badge bg-warning text-dark me-2" style={{ fontSize: 10 }}
                        title={savedById[b._id] ? 'You have edited this block. Click Save to persist.' : 'New block — not yet saved.'}>
                        <i className="bi bi-pencil-fill me-1"/>{savedById[b._id] ? 'Modified' : 'New'}
                      </span>
                    )}
                  </div>
                  <div className="d-flex gap-1">
                    {canEdit && b.type === 'text' && (
                      <button className="btn btn-outline-secondary btn-sm py-0 px-2" onClick={() => togglePreview(b._id)}
                        title={previewIds.has(b._id) ? 'Switch to edit (raw HTML)' : 'Switch to preview (rendered)'}>
                        {previewIds.has(b._id)
                          ? <><i className="bi bi-pencil me-1"/>Edit</>
                          : <><i className="bi bi-eye me-1"/>Preview</>}
                      </button>
                    )}
                    {canEdit && (
                      <button className="btn btn-outline-danger btn-sm py-0 px-1" onClick={() => removeBlock(b._id)} title="Remove block">
                        <i className="bi bi-x"/>
                      </button>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <input className="form-control form-control-sm mb-1" placeholder="Block title (optional)" value={b.title}
                    onChange={e => updateBlock(b._id, { title: e.target.value })} />
                )}
                {(b.type === 'text' || b.type === 'embed' || b.type === 'code') && (
                  // Editors get a textarea for raw HTML by default; for text blocks
                  // they can toggle a Preview that renders the HTML inline so
                  // images and formatting are visible without leaving edit mode.
                  canEdit && !(b.type === 'text' && previewIds.has(b._id)) ? (
                    <textarea className="form-control form-control-sm" rows={b.type === 'text' ? 8 : 5}
                      value={b.content} onChange={e => updateBlock(b._id, { content: e.target.value })}
                      placeholder={b.type === 'text' ? 'HTML content (h2, p, ul, li, strong, em ...)' : b.type === 'code' ? 'Paste code here' : 'HTML to embed (iframe, etc.)'}
                      style={{ fontFamily: b.type === 'code' || b.type === 'embed' ? 'ui-monospace, Menlo, monospace' : undefined }} />
                  ) : (
                    b.type === 'text'
                      ? <div className="border rounded p-2" style={{ background: '#fff', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: b.content }} />
                      : <pre className="small mb-0">{b.content}</pre>
                  )
                )}
                {(b.type === 'image' || b.type === 'video' || b.type === 'pdf' || b.type === 'link') && (
                  canEdit ? (
                    <input className="form-control form-control-sm" placeholder={`${b.type} URL`} value={b.content}
                      onChange={e => updateBlock(b._id, { content: e.target.value })} />
                  ) : (
                    <a href={b.content} target="_blank" rel="noopener noreferrer">{b.content}</a>
                  )
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// REUSE DIALOG: pick another course → optionally a module → optionally a topic
// ════════════════════════════════════════════════════════════════════════════
function ReuseDialog({ course, onClose, reloadCourse, setToast }) {
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [sourceCourseId, setSourceCourseId] = useState('');
  const [sourceCourse, setSourceCourse]     = useState(null);
  const [mode, setMode]             = useState('course'); // 'course'|'module'|'topic'
  const [sourceModuleId, setSourceModuleId] = useState('');
  const [sourceTopicId,  setSourceTopicId]  = useState('');
  const [targetModuleId, setTargetModuleId] = useState('');
  const [importing, setImporting]   = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    // Pull every course in the tenant — `paginated()` returns the array at
    // r.data.records. Don't filter out the current course; we just disable it
    // in the dropdown so the user sees it's there.
    api.get('/education/courses', { params: { limit: 500 } })
      .then(r => {
        const list = r.data?.records || r.data?.data?.records || r.data?.data || [];
        if (!Array.isArray(list)) {
          setLoadError(`Unexpected response shape: ${JSON.stringify(r.data).slice(0, 120)}…`);
          setAllCourses([]);
        } else {
          setAllCourses(list);
        }
      })
      .catch(e => {
        setLoadError(e.response?.data?.message || e.message || 'Failed to load courses.');
        setAllCourses([]);
      })
      .finally(() => setLoading(false));
  }, [course._id]);

  // Pool the user can actually reuse from = everything except the current course.
  const reusable = allCourses.filter(c => c._id !== course._id);

  useEffect(() => {
    setSourceCourse(null); setSourceModuleId(''); setSourceTopicId('');
    if (!sourceCourseId) return;
    api.get(`/education/courses/${sourceCourseId}`)
      .then(r => setSourceCourse(r.data?.data || r.data))
      .catch(() => {});
  }, [sourceCourseId]);

  const sourceModule = sourceCourse?.modules?.find(m => m._id === sourceModuleId);

  const doImport = async () => {
    if (!sourceCourseId) return setToast({ type: 'danger', msg: 'Pick a source course.' });
    if (mode === 'module' && !sourceModuleId) return setToast({ type: 'danger', msg: 'Pick a source module.' });
    if (mode === 'topic') {
      if (!sourceModuleId || !sourceTopicId) return setToast({ type: 'danger', msg: 'Pick source module and topic.' });
      if (!targetModuleId) return setToast({ type: 'danger', msg: 'Pick a target module on this course.' });
    }
    setImporting(true);
    try {
      const r = await api.post(`/education/courses/${course._id}/reuse`, { sourceCourseId, mode, sourceModuleId, sourceTopicId, targetModuleId });
      setToast({ type: 'success', msg: r.data?.message || r.data?.data?.summary || 'Imported.' });
      reloadCourse();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Import failed.' });
    }
    setImporting(false);
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><i className="bi bi-arrow-repeat me-2"/>Reuse content from another course</h5>
            <button className="btn-close" onClick={onClose}/>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-3"><div className="spinner-border text-primary"/></div>
            ) : (
              <>
                {loadError && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <i className="bi bi-exclamation-triangle me-1"/>{loadError}
                  </div>
                )}
                {reusable.length === 0 && !loadError && (
                  <div className="alert alert-warning py-2 small mb-3">
                    <i className="bi bi-info-circle me-1"/>
                    No other courses to reuse from. Create at least one more course (different class, subject, or year)
                    and add some modules/topics — then come back here to copy from it.
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">
                      Source course
                      <span className="text-muted small ms-2">
                        ({reusable.length} available {allCourses.length !== reusable.length && '· current course excluded'})
                      </span>
                    </label>
                    <select className="form-select" value={sourceCourseId} onChange={e => setSourceCourseId(e.target.value)}
                      disabled={reusable.length === 0}>
                      <option value="">— Pick a course —</option>
                      {reusable.map(c => (
                        <option key={c._id} value={c._id}>
                          {c.title}{c.classGrade ? ` · ${c.classGrade}` : ''}{c.subject ? ` · ${c.subject}` : ''}
                          {(c.moduleCount != null || c.topicCount != null) && ` (${c.moduleCount || 0} mod, ${c.topicCount || 0} topics)`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label">What to copy</label>
                    <div className="d-flex flex-wrap gap-2">
                      {[
                        { v: 'course', label: 'Whole course (every module)', icon: 'bi-collection' },
                        { v: 'module', label: 'One module',                  icon: 'bi-folder' },
                        { v: 'topic',  label: 'One topic',                    icon: 'bi-file-text' },
                      ].map(o => (
                        <button key={o.v} type="button"
                          className={`btn btn-sm ${mode === o.v ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setMode(o.v)}>
                          <i className={`bi ${o.icon} me-1`}/>{o.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {(mode === 'module' || mode === 'topic') && sourceCourse && (
                    <div className="col-md-6">
                      <label className="form-label">
                        Source module
                        <span className="text-muted small ms-2">({(sourceCourse.modules || []).length} in course)</span>
                      </label>
                      {(sourceCourse.modules || []).length === 0 ? (
                        <div className="alert alert-warning py-2 small mb-0">
                          <i className="bi bi-info-circle me-1"/>The selected course has no modules yet.
                        </div>
                      ) : (
                        <select className="form-select" value={sourceModuleId}
                          onChange={e => { setSourceModuleId(e.target.value); setSourceTopicId(''); }}>
                          <option value="">— Pick a module —</option>
                          {(sourceCourse.modules || []).map(m => (
                            <option key={m._id} value={m._id}>
                              {m.title} ({(m.topics || []).length} topic{(m.topics || []).length === 1 ? '' : 's'})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {mode === 'topic' && sourceModule && (
                    <div className="col-md-6">
                      <label className="form-label">
                        Source topic
                        <span className="text-muted small ms-2">({(sourceModule.topics || []).length} in module)</span>
                      </label>
                      {(sourceModule.topics || []).length === 0 ? (
                        <div className="alert alert-warning py-2 small mb-0">
                          <i className="bi bi-info-circle me-1"/>The selected module has no topics yet.
                        </div>
                      ) : (
                        <select className="form-select" value={sourceTopicId} onChange={e => setSourceTopicId(e.target.value)}>
                          <option value="">— Pick a topic —</option>
                          {(sourceModule.topics || []).map(t => (
                            <option key={t._id} value={t._id}>{t.title}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {mode === 'topic' && (
                    <div className="col-12">
                      <label className="form-label">Target module on <em>this</em> course</label>
                      {(course.modules || []).length === 0 ? (
                        <div className="alert alert-warning py-2 small mb-0">
                          <i className="bi bi-info-circle me-1"/>This course has no modules yet — close this dialog,
                          add a module first, then come back to copy a topic into it.
                        </div>
                      ) : (
                        <select className="form-select" value={targetModuleId} onChange={e => setTargetModuleId(e.target.value)}>
                          <option value="">— Pick a target module —</option>
                          {(course.modules || []).map(m => (
                            <option key={m._id} value={m._id}>{m.title}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>

                <div className="alert alert-info mt-3 small mb-0">
                  <i className="bi bi-info-circle me-1"/>
                  Content is <strong>copied</strong>, not linked. You can edit the imported content freely without affecting the source.
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={doImport} disabled={importing}>
              {importing && <span className="spinner-border spinner-border-sm me-1"/>}Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
